import * as turf from "@turf/turf";
import { UrlHashHandler } from "../../shared/UrlHashHandler";
import { get, writable } from "svelte/store";
import StyleParts from "../../shared/StyleParts";
import { appManager } from "../../AppManager";
import LeftDrivingCountry from "../../shared/LeftDrivingCountry";
import { TripsScenarioManager } from "../../map/controls/layers/trips/TripsScenarioManager";
import { MapManager } from "../../lib/maplibre/MapManager";
export class SharedScenarioManager {
    constructor(id) {
        this.customSourceId = "anyways-app";
        this.scenarioStore = writable(undefined);
        this.branchCommitId = writable(undefined);
        this.referenceBranchCommitId = writable(undefined);
        this.isLeftSideDriveStore = writable(false);
        this.tripsLayerManagers = [];
        this.profiles = ["bicycle.comfort_safety", "bicycle.comfort", "bicycle.commute", "bicycle.fast", "bicycle.safety", "bicycle.short", "car.fast", "car.short",
            "car.classifications", "bigtruck.fast", "bigtruck.short", "pedestrian.short", "pedestrian.slow_roads"];
        this.id = id;
        this.mapHashHandler = new UrlHashHandler("map");
    }
    getMap() {
        return this.map;
    }
    getBranchCommitIdStore() {
        return this.branchCommitId;
    }
    getReferenceBranchCommitIdStore() {
        return this.referenceBranchCommitId;
    }
    getScenarioDataStore() {
        return this.scenarioStore;
    }
    getIsLeftSideDriveStore() {
        return this.isLeftSideDriveStore;
    }
    /**
     * Initializes the scenario manager.
     * @param mapContainer
     */
    async initialize(mapContainer, baseLayer) {
        // make sure the shared scenario data is there.
        const data = await appManager.api.projects.getSharedScenario({
            id: this.id
        });
        // get branch commit it.
        const branchCommitId = await appManager.api.edit.getLatestCommitForBranch({
            branchId: data.scenario.branchId
        });
        this.branchCommitId.set(branchCommitId);
        // initialize the map.
        let zoom = 10.51;
        const area = data.scenario.project.area;
        const bbox = turf.bbox(area);
        let center = turf.center(area).geometry.coordinates;
        let bounds;
        // get map state if present, otherwise use the scenario bbox.
        const mapState = this.mapHashHandler.getState();
        if (typeof mapState !== "undefined") {
            const parts = mapState.split("/");
            if (parts.length === 3) {
                center = [parseFloat(parts[1]), parseFloat(parts[2])];
                zoom = parseFloat(parts[0]);
            }
        }
        else {
            bounds = [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
        }
        const sourceUrl = appManager.api.publish.buildBranchCommitMvt(get(this.branchCommitId));
        const mapOptions = {
            imageryLayers: [
                {
                    source: {
                        type: "raster",
                        tiles: [
                            "https://geo.api.vlaanderen.be/GRB/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=grb_bsk&STYLE=&FORMAT=image/png&tileMatrixSet=GoogleMapsVL&tileMatrix={z}&tileRow={y}&tileCol={x}"
                        ],
                        tileSize: 256,
                        attribution: "AIV",
                    },
                    layer: {
                        id: "background-imagery",
                        type: "raster",
                        source: "background-imagery-source",
                        minzoom: 0,
                        maxzoom: 20,
                        layout: {
                            visibility: "none",
                        },
                    },
                },
            ],
            baseLayer: baseLayer,
            customSource: {
                sourceId: this.customSourceId,
                source: {
                    type: "vector",
                    url: sourceUrl,
                },
            },
        };
        this.map = new MapManager(mapOptions);
        await this.map.initialize(mapContainer, async () => {
            this.isLeftSideDriveStore.set(LeftDrivingCountry.isInLeftDrivingCountry(this.map.getView().center));
            StyleParts.addConstructionWorksLayer(this.map, this.customSourceId);
            StyleParts.addDetoursLayer(this.map, this.customSourceId);
            StyleParts.addCycleStreets(this.map, this.customSourceId);
            this.tripsLayerManagers.forEach(m => m.addToMap(this.map));
            // initialize the trip scenario manager.
            const tripScenarioManager = new TripsScenarioManager(this, data.scenario.tripScenarioModel, {
                profiles: []
            });
            tripScenarioManager.addToMap(this.map);
        }, {
            center: center,
            zoom: zoom,
            bounds: bounds
        });
        // hook up listeners.
        this.map.on("zoomend", () => {
            this.updateMapHash();
            this.isLeftSideDriveStore.set(LeftDrivingCountry.isInLeftDrivingCountry(this.map.getView().center));
        });
        this.map.on("dragend", () => {
            this.updateMapHash();
            this.isLeftSideDriveStore.set(LeftDrivingCountry.isInLeftDrivingCountry(this.map.getView().center));
        });
    }
    updateMapHash() {
        const view = this.map.getView();
        this.mapHashHandler.update(`${view.zoom.toFixed(2)}/${view.center.lng.toFixed(5)}/${view.center.lat.toFixed(5)}`, true);
    }
}
